/**
 * Live preview of the operator website based on user config (colors + images).
 * Matches the Hi-Tech Roadlinks–style layout: header, hero, features, about, services,
 * attractions, routes, offer banner, testimonials, fleet, contact, footer.
 */

import React from "react";
import { Row, Col } from 'antd';
import { CheckCircleFilled } from "@ant-design/icons";
import { CustomFooter } from './custom-footer/custom-footer.component';

const GCS_BASE_URL = 'https://storage.googleapis.com/operator-websites';
export const DEFAULT_COLORS = {
    primary_color: '#0A2351',
    button_color: '#06C7E2',
    button_text_color: '#FFFFFF',
    bg_color: '#F4F2F0',
    discount_banner_color: '#023F82',
    home_bg_color: '#FFFFFF',
    discount_banner_txt: '#FFFFFF',
    discount_banner_highlight: '#FFD700',
    footer_bg_color: '#01161A',
    contact_bg_color: '#064598',
    menu_bg_color: '#0A2351',
    menu_txt_color: '#FFFFFF',
    banner_header: '#FFFFFF',
    banner_description: '#FFD700',
};

const isLogoType = (key) => !key || ['logo', 'agent-portal-logo', 'favicon'].includes(key) || /logo|favicon/i.test(key);

const getImageSrc = (imageBaseUrl, imageType, ext = null) => {
    if (!imageBaseUrl) return null;
    const e = ext || (isLogoType(imageType) ? '.png' : '.webp');
    return `${imageBaseUrl}/${imageType}${e}`;
};

/** Resolve image URL: use preview override (selected-but-not-uploaded) if present, else GCS. */
const resolveImageUrl = (imagePreviewOverrides, imageBaseUrl, imageType) => {
    const override = imagePreviewOverrides && imagePreviewOverrides[imageType];
    if (override && typeof override === 'string') return override;
    return getImageSrc(imageBaseUrl, imageType);
};

const PreviewImage = ({ src, alt, placeholderStyle, className }) => {
    const [failed, setFailed] = React.useState(false);
    React.useEffect(() => {
        setFailed(false);
    }, [src]);
    if (!src || failed) {
        return <div className={className} style={{ ...placeholderStyle, minWidth: 80, minHeight: 60 }} />;
    }
    return (
        <img
            src={src}
            alt={alt}
            className={className}
            onError={() => setFailed(true)}
        />
    );
};

const contactInfo = () => {
    return (
        <div className="contact-txt-root">
            <div className="contant-txt">
                Reach us
            </div>
            <div className="booking-suggestion-txt">
                For Bookings, Suggestions, Complaints & Boarding points
            </div>
            <div className="phone-no-txt">
                {+91-1234567890} |<a style= {{display: 'inline-block'}} href={`mailto:support@example.com`}>Email us: support@example.com</a>
            </div>
        </div>
    )
}

export const OperatorWebsitePreview = ({ websiteColors = {}, imageBaseUrl = '', imagePreviewOverrides = {}, variant = 'desktop' }) => {
    const c = { ...DEFAULT_COLORS, ...websiteColors };
    const discountBg = c.discount_linear_bg && c.discount_linear_bg.trim()
        ? c.discount_linear_bg
        : c.discount_banner_color;
    const contactBg = c.contact_linear_bg && c.contact_linear_bg.trim()
        ? c.contact_linear_bg
        : c.contact_bg_color;

    const logoUrl = resolveImageUrl(imagePreviewOverrides, imageBaseUrl, 'logo');
    const heroUrl = resolveImageUrl(imagePreviewOverrides, imageBaseUrl, 'home-banner');
    const aboutUrl = resolveImageUrl(imagePreviewOverrides, imageBaseUrl, 'home-page-background');
    const contactImgUrl = resolveImageUrl(imagePreviewOverrides, imageBaseUrl, 'home-contact-image');
    const fleetUrls = [1, 2, 3, 4].map((i) => resolveImageUrl(imagePreviewOverrides, imageBaseUrl, `fleet-image${i}`));

    const isMobile = variant === 'mobile';
    return (
        <div className={`operator-website-preview${isMobile ? ' operator-website-preview--mobile' : ''}`} style={{ background: c.home_bg_color }}>
            {/* Header */}
            <header
                className="owp-header owp-header--sticky"
                style={{
                    background: c.menu_bg_color,
                    color: c.menu_txt_color,
                }}
            >
                <div className="owp-header-inner">
                    <div className="owp-logo">
                        {logoUrl ? (
                            <PreviewImage src={logoUrl} alt="Logo" className="owp-logo-img" />
                        ) : (
                            <span className="owp-logo-placeholder">Logo</span>
                        )}
                    </div>
                    <nav className="owp-nav">
                        <a href="#home">Home</a>
                        <a href="#about">About us</a>
                        <a href="#contact">Contact</a>
                        <a href="#manage">Manage Bookings</a>
                    </nav>
                    <div className="owp-header-hamburger" aria-hidden>
                        <span /><span /><span />
                    </div>
                    <button
                        type="button"
                        className="owp-btn owp-btn-header"
                        style={{
                            background: c.button_color,
                            color: c.button_text_color,
                        }}
                    >
                        Sign Up Now
                    </button>
                </div>
            </header>

            {/* Hero */}
            <section
                className="owp-hero"
                style={{
                    backgroundImage: heroUrl ? `url(${heroUrl})` : `linear-gradient(135deg, ${c.primary_color} 0%, #1e3a5f 100%)`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                }}
            >
                <div className="owp-hero-overlay">
                    <h1 className="owp-hero-title" style={{ color: c.banner_header }}>
                        Premium Bus Travel. Booked in Seconds.
                    </h1>
                    <p className="owp-hero-subtitle" style={{ color: c.banner_description }}>
                        Luxury Bus for your next journey.
                    </p>
                    <div className="owp-hero-form">
                        <input type="text" placeholder="From" readOnly />
                        <input type="text" placeholder="To" readOnly />
                        <input type="text" placeholder="Date" readOnly />
                        <input type="text" placeholder="Add Return" readOnly />
                        <button
                            type="button"
                            className="owp-btn owp-btn-search"
                            style={{
                                background: c.button_color,
                                color: c.button_text_color,
                            }}
                        >
                            Search Now
                        </button>
                    </div>
                </div>
            </section>

            {/* Feature strip */}
            <section className="owp-features" style={{ background: c.home_bg_color }}>
                <div className="owp-features-inner" grid={{ gutter: [24, 24] }}>
                    {['Instant Booking', 'Best Prices', 'Secure Payments', 'Bus Tracking', 'Instant Refunds'].map((label) => (
                        <Col span={isMobile ? 8 : 5}>
                        <span key={label} className="owp-feature-item" style={{ color: c.primary_color }}>
                            <span className="owp-feature-check"><CheckCircleFilled style={{ fontSize: '20px', color: c.primary_color }} /></span> {label}
                        </span>
                        </Col>
                    ))}
                </div>
            </section>

            {/* Special offer banner */}
            <section className="owp-offer-banner">
                <div className={`discount-banner-wrapper${isMobile ? ' discount-banner-wrapper--mobile' : ''}`}>
                    <div className="discount-banner" style={{ background: discountBg }}>
                        <div className="content">
                            <h2 style={{ color: c.discount_banner_txt }}>
                                Book Now!
                                Don't Miss The
                                Special Offer
                                <span className="highlight">
                                    <span style={{ color: c.discount_banner_highlight }}>10% Discount</span>
                                </span>
                            </h2>
                            <p style={{ color: c.discount_banner_txt }}>Give your users a quick insight into what your bus service offers.</p>
                            <button className="book-btn" style={{ background: c.button_color, color: c.button_text_color }}>Book Now!</button>
                        </div>
                        <div className="illustration">
                            <img
                                src="https://storage.googleapis.com/customer-website-data/common-files/discount-icon.webp"
                                alt="Calendar illustration"
                                loading="lazy"
                                decoding="async"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* About */}
            <section className="owp-about" style={{ background: c.home_bg_color }}>
                <div className="owp-about-inner">
                    <div className="owp-about-text">
                        <h2 className="owp-section-title" style={{ color: c.primary_color }}>
                            About {global.operatorName} – Your Travel Made Simple!
                        </h2>
                        <p className="owp-about-body">
                            Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris.
                        </p>
                        <button
                            type="button"
                            className="owp-btn"
                            style={{
                                background: c.button_color,
                                color: c.button_text_color,
                            }}
                        >
                            Read More
                        </button>
                    </div>
                    <div className="owp-about-image">
                        {aboutUrl ? (
                            <PreviewImage src={aboutUrl} alt="About" placeholderStyle={{ background: c.bg_color, width: '300px', height: '300px', objectFit: 'cover' }} />
                        ) : (
                            <div className="owp-img-placeholder" style={{ background: c.bg_color, width: '300px', height: '300px', objectFit: 'cover' }} />
                        )}
                    </div>
                </div>
            </section>

            {/* Services */}
            {/* <section className="owp-services" style={{ background: c.home_bg_color }}>
                <h2 className="owp-section-title" style={{ color: c.primary_color }}>
                    We Offer Top-Notch Services for Every Travel Need!
                </h2>
                <p className="owp-section-subtitle">Choose from a range of services designed for your comfort.</p>
                <div className="owp-services-grid">
                    {[
                        { title: 'Search Your Route', text: 'Find buses between cities easily.' },
                        { title: 'Book in Seconds', text: 'Quick and secure booking.' },
                        { title: 'Track Your Bus', text: 'Real-time bus tracking.' },
                        { title: '24/7 Support', text: 'We are here to help you.' },
                    ].map(({ title, text }) => (
                        <div key={title} className="owp-service-card" style={{ borderColor: c.primary_color }}>
                            <h3 style={{ color: c.primary_color }}>{title}</h3>
                            <p>{text}</p>
                        </div>
                    ))}
                </div>
            </section> */}

            {/* Attractions */}
            {/* <section className="owp-attractions" style={{ background: c.home_bg_color }}>
                <h2 className="owp-section-title" style={{ color: c.primary_color }}>
                    Discover Fariza's Top Attractions
                </h2>
                <p className="owp-section-subtitle">Explore popular destinations.</p>
                <div className="owp-attractions-circle-wrap">
                    {[1, 2, 3, 4, 5].map((i) => (
                        <div key={i} className="owp-attraction-circle" style={{ background: c.bg_color }} />
                    ))}
                </div>
            </section> */}

            {/* Popular routes */}
            <section className="owp-routes" style={{ background: c.home_bg_color }}>
                <h2 className="owp-section-title" style={{ color: c.primary_color }}>
                    Popular Bus Routes
                </h2>
                <p className="owp-section-subtitle">Select a route to see schedules.</p>
                <div className="owp-route-tags">
                    {['Ahmednagar to Chennai', 'Bangalore to Delhi', 'Mumbai to Goa', 'Chennai to Bangalore', 'Delhi to Mumbai'].map((r) => (
                        <span key={r} className="owp-route-tag">{r}</span>
                    ))}
                </div>
                <button
                    type="button"
                    className="owp-btn"
                    style={{
                        background: c.button_color,
                        color: c.button_text_color,
                    }}
                >
                    View More Bus Routes
                </button>
            </section>


            {/* Testimonials */}
            {/* <section className="owp-testimonials" style={{ background: c.home_bg_color }}>
                <h2 className="owp-section-title" style={{ color: c.primary_color }}>
                    What Our Travelers Are Saying!
                </h2>
                <p className="owp-section-subtitle">Real experiences from real travelers.</p>
                <div className="owp-testimonials-grid">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="owp-testimonial-card" style={{ borderColor: c.primary_color }}>
                            <span className="owp-quote">"</span>
                            <p>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Great service and comfortable buses.</p>
                            <a href="#read" style={{ color: c.primary_color }}>Read More</a>
                        </div>
                    ))}
                </div>
            </section> */}

            {/* Fleet */}
            <section className="owp-fleet" style={{ background: c.home_bg_color }}>
                <h2 className="owp-section-title" style={{ color: c.primary_color }}>
                    Our Fleet in Focus
                </h2>
                <p className="owp-section-subtitle">Modern, safe buses for your journey.</p>
                <div className="owp-fleet-grid">
                    {fleetUrls.map((url, i) => (
                        <div key={i} className="owp-fleet-item">
                            {url ? (
                                <PreviewImage src={url} alt={`Fleet ${i + 1}`} placeholderStyle={{ background: c.bg_color }} />
                            ) : (
                                <div className="owp-img-placeholder" style={{ background: c.bg_color }} />
                            )}
                        </div>
                    ))}
                </div>
            </section>

            {/* Contact banner */}
            <section
                className="owp-contact-banner"
            >
                <div className="home-page-contact-root" style={{ background: contactBg }}>
                    {contactImgUrl && variant == 'desktop'?<Row type="flex">
                        <Col order={1} span={isMobile ? 22 : 14}>
                        {contactInfo()}
                        </Col>
                        <Col order={2} span={isMobile ? 24 : 10}>
                        <div className='home-contact-image' style={{ display: isMobile ? 'none' : 'block', backgroundImage: `url(${contactImgUrl})` }}></div>
                        </Col>
                    </Row>:
                    contactInfo()}
                </div>
            </section>

            {/* Footer */}
            <CustomFooter customColors={c} variant={variant} />
        </div>
    );
};
